import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { ShiftMap } from '../components/ShiftMap';
import axios from 'axios';
import {
  Calendar, Clock, MapPin, User, Plus, X, ChevronDown, ChevronUp,
  CheckCircle2, AlertCircle, Search, Navigation, LogIn, LogOut, AlertTriangle,
  List, LayoutGrid, Bookmark, Trash2, Save, Sparkles, Star, Radio, Users, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import ShiftCalendar from '../components/ShiftCalendar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ShiftManagement() {
  const { user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [expandedShift, setExpandedShift] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [clockingIn, setClockingIn] = useState(null);
  const [clockingOut, setClockingOut] = useState(null);
  const [noShowAlerts, setNoShowAlerts] = useState([]);
  const [showNoShows, setShowNoShows] = useState(false);
  const [viewMode, setViewMode] = useState('calendar');
  const [calendarShifts, setCalendarShifts] = useState([]);
  const [calendarRange, setCalendarRange] = useState({ from: '', to: '' });
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [conflictChecking, setConflictChecking] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [broadcastingShift, setBroadcastingShift] = useState(null);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastUrgency, setBroadcastUrgency] = useState('normal');
  const [viewBidsShift, setViewBidsShift] = useState(null);
  const [bids, setBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [aiRecommendation, setAiRecommendation] = useState(null);

  const [shiftForm, setShiftForm] = useState({
    client_id: '', date: '', start_time: '', end_time: '',
    notes: '', rate: '', address: '', latitude: null, longitude: null,
    visibility: 'network'
  });

  const isAdmin = ['agency_admin', 'ceo'].includes(user?.role);

  useEffect(() => {
    fetchShifts();
    if (isAdmin) {
      fetchClients();
      fetchCaregivers();
      fetchTemplates();
    }
  }, [statusFilter, dateFilter]);

  const fetchShifts = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (dateFilter) params.append('date', dateFilter);
      const { data } = await axios.get(`${API_URL}/api/shifts?${params.toString()}`, { withCredentials: true });
      setShifts(data);
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/clients`, { withCredentials: true });
      setClients(data);
    } catch (err) { console.error(err); }
  };

  const fetchCaregivers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/caregivers`, { withCredentials: true });
      setCaregivers(data);
    } catch (err) { console.error(err); }
  };

  const fetchNoShowAlerts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shifts/no-show-alerts`, { withCredentials: true });
      setNoShowAlerts(data);
    } catch (err) { console.error(err); }
  };

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shift-templates`, { withCredentials: true });
      setTemplates(data);
    } catch (err) { console.error(err); }
  };

  const applyTemplate = (t) => {
    setShiftForm(f => ({
      ...f,
      start_time: t.start_time,
      end_time: t.end_time,
      rate: t.rate || f.rate,
      address: t.address || f.address,
      notes: t.notes || f.notes,
    }));
    toast.success(`Template "${t.name}" applied`);
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) { toast.error('Enter a template name'); return; }
    try {
      await axios.post(`${API_URL}/api/shift-templates`, {
        name: templateName.trim(),
        start_time: shiftForm.start_time,
        end_time: shiftForm.end_time,
        rate: shiftForm.rate,
        address: shiftForm.address,
        notes: shiftForm.notes,
      }, { withCredentials: true });
      toast.success('Template saved');
      setShowSaveTemplate(false);
      setTemplateName('');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
  };

  const deleteTemplate = async (id, name) => {
    try {
      await axios.delete(`${API_URL}/api/shift-templates/${id}`, { withCredentials: true });
      toast.success(`Template "${name}" deleted`);
      fetchTemplates();
    } catch (err) { toast.error('Failed to delete template'); }
  };

  const checkConflicts = async (date, start_time, end_time, client_id, caregiver_id) => {
    if (!date || !start_time || !end_time) { setConflicts([]); return; }
    setConflictChecking(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/shifts/check-conflicts`, {
        date, start_time, end_time, client_id, caregiver_id
      }, { withCredentials: true });
      setConflicts(data.conflicts || []);
    } catch { setConflicts([]); }
    setConflictChecking(false);
  };

  // Debounced conflict check when form fields change
  useEffect(() => {
    if (!showCreateDialog) { setConflicts([]); return; }
    const timer = setTimeout(() => {
      checkConflicts(shiftForm.date, shiftForm.start_time, shiftForm.end_time, shiftForm.client_id);
    }, 500);
    return () => clearTimeout(timer);
  }, [shiftForm.date, shiftForm.start_time, shiftForm.end_time, shiftForm.client_id, showCreateDialog]);

  const getGPSPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) { reject(new Error('Geolocation not supported')); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  };

  const handleClockIn = async (shiftId) => {
    setClockingIn(shiftId);
    try {
      const coords = await getGPSPosition();
      await axios.put(`${API_URL}/api/shifts/${shiftId}/check-in`, coords, { withCredentials: true });
      toast.success('Clocked in successfully');
      fetchShifts();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Clock-in failed';
      toast.error(msg);
    }
    setClockingIn(null);
  };

  const handleClockOut = async (shiftId) => {
    setClockingOut(shiftId);
    try {
      const coords = await getGPSPosition();
      const { data } = await axios.put(`${API_URL}/api/shifts/${shiftId}/check-out`, coords, { withCredentials: true });
      toast.success(`Clocked out. Hours worked: ${data.hours_worked?.toFixed(2) || 'N/A'}`);
      fetchShifts();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Clock-out failed';
      toast.error(msg);
    }
    setClockingOut(null);
  };

  const handleResolveNoShow = async (alertId, resolution) => {
    try {
      await axios.put(`${API_URL}/api/shifts/no-show-alerts/${alertId}/resolve`, { resolution }, { withCredentials: true });
      toast.success('No-show alert resolved');
      fetchNoShowAlerts();
    } catch { toast.error('Failed to resolve'); }
  };

  useEffect(() => {
    if (isAdmin) fetchNoShowAlerts();
  }, []);

  // Fetch shifts for calendar date range
  const fetchCalendarShifts = async (from, to) => {
    try {
      const params = new URLSearchParams();
      if (from) params.append('date_from', from);
      if (to) params.append('date_to', to);
      if (statusFilter) params.append('status', statusFilter);
      const { data } = await axios.get(`${API_URL}/api/shifts?${params.toString()}`, { withCredentials: true });
      setCalendarShifts(data);
    } catch (err) { console.error('Failed to fetch calendar shifts:', err); }
  };

  // Watch for calendar range changes
  useEffect(() => {
    if (viewMode === 'calendar') {
      const el = document.querySelector('[data-testid="cal-range"]');
      if (el) {
        const from = el.getAttribute('data-from');
        const to = el.getAttribute('data-to');
        if (from && to && (from !== calendarRange.from || to !== calendarRange.to)) {
          setCalendarRange({ from, to });
          fetchCalendarShifts(from, to);
        }
      }
    }
  });

  // Initial calendar load
  useEffect(() => {
    if (viewMode === 'calendar') {
      const now = new Date();
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const mon = new Date(now);
      mon.setDate(diff);
      const sun = new Date(mon);
      sun.setDate(mon.getDate() + 6);
      const from = mon.toISOString().split('T')[0];
      const to = sun.toISOString().split('T')[0];
      fetchCalendarShifts(from, to);
    }
  }, [viewMode, statusFilter]);

  const handleReschedule = async (shiftId, newDate) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/shifts/${shiftId}`, { date: newDate }, { withCredentials: true });
      if (data.conflicts?.length > 0) {
        toast.warning(`Shift rescheduled to ${newDate} — ${data.conflicts.length} conflict(s) detected`, { duration: 5000 });
      } else {
        toast.success(`Shift rescheduled to ${newDate}`);
      }
      fetchShifts();
      if (calendarRange.from && calendarRange.to) {
        fetchCalendarShifts(calendarRange.from, calendarRange.to);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    }
  };

  const handleCalendarShiftClick = (shift) => {
    setExpandedShift(expandedShift === shift.id ? null : shift.id);
    if (viewMode === 'calendar') {
      setViewMode('list');
      setDateFilter(shift.date);
    }
  };

  const handleGeocodeAddress = async () => {
    if (!shiftForm.address) { toast.error('Enter an address first'); return; }
    setGeocoding(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/shifts/geocode`, { address: shiftForm.address }, { withCredentials: true });
      if (data.success) {
        setShiftForm(prev => ({ ...prev, latitude: data.latitude, longitude: data.longitude }));
        toast.success('Address located on map');
      } else {
        toast.error('Could not locate address — try a more specific address');
      }
    } catch (err) {
      toast.error('Geocoding failed');
    } finally {
      setGeocoding(false);
    }
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    if (!shiftForm.client_id || !shiftForm.date || !shiftForm.start_time || !shiftForm.end_time) {
      toast.error('Client, date, start time and end time are required');
      return;
    }
    try {
      const payload = {
        ...shiftForm,
        rate: shiftForm.rate ? parseFloat(shiftForm.rate) : null,
      };
      const { data } = await axios.post(`${API_URL}/api/shifts`, payload, { withCredentials: true });
      if (data.conflicts?.length > 0) {
        toast.warning(`Shift created with ${data.conflicts.length} conflict(s)`, { duration: 5000 });
      } else {
        toast.success('Shift created successfully');
      }
      setShowCreateDialog(false);
      setShiftForm({ client_id: '', date: '', start_time: '', end_time: '', notes: '', rate: '', address: '', latitude: null, longitude: null, visibility: 'network' });
      setConflicts([]);
      fetchShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create shift');
    }
  };


  const fetchSuggestions = async (shiftId) => {
    setSuggestLoading(true);
    setShowSuggestions(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/shifts/${shiftId}/suggest-caregivers`, { withCredentials: true });
      setSuggestions(data);
    } catch (err) {
      toast.error('Failed to get suggestions');
      setSuggestions(null);
    } finally { setSuggestLoading(false); }
  };

  const handleAssignFromSuggestion = async (shiftId, caregiverId) => {
    try {
      await axios.put(`${API_URL}/api/shifts/${shiftId}/admin-assign`, { caregiver_id: caregiverId }, { withCredentials: true });
      toast.success('Caregiver assigned');
      setShowSuggestions(false);
      setSuggestions(null);
      fetchShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleAssignCaregiver = async (shiftId, caregiverId) => {
    const cg = caregivers.find(c => c.id === caregiverId);
    try {
      await axios.put(`${API_URL}/api/shifts/${shiftId}`, {
        caregiver_id: caregiverId,
        caregiver_name: cg?.name || '',
        status: 'assigned'
      }, { withCredentials: true });
      toast.success(`Shift assigned to ${cg?.name || 'caregiver'}`);
      fetchShifts();
    } catch (err) {
      toast.error('Failed to assign shift');
    }
  };

  const handleBroadcastShift = async (shiftId) => {
    try {
      await axios.put(`${API_URL}/api/shifts/${shiftId}/broadcast`, {
        urgency: broadcastUrgency,
        message: broadcastMsg.trim(),
      }, { withCredentials: true });
      toast.success('Shift broadcast to all caregivers');
      setBroadcastingShift(null);
      setBroadcastMsg('');
      setBroadcastUrgency('normal');
      fetchShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to broadcast');
    }
  };

  const handleViewBids = async (shiftId) => {
    setViewBidsShift(shiftId);
    setBidsLoading(true);
    setAiRecommendation(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/shifts/${shiftId}/bids`, { withCredentials: true });
      setBids(data.bids || []);
      setAiRecommendation(data.ai_recommendation || null);
    } catch (err) {
      toast.error('Failed to fetch bids');
      setBids([]);
    } finally { setBidsLoading(false); }
  };

  const handleAcceptBid = async (shiftId, bidId, caregiverName) => {
    try {
      await axios.put(`${API_URL}/api/shifts/${shiftId}/bids/${bidId}/accept`, {}, { withCredentials: true });
      toast.success(`Shift assigned to ${caregiverName}`);
      setViewBidsShift(null);
      setBids([]);
      fetchShifts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept bid');
    }
  };

  const statusColors = {
    open: '#4A90E2',
    assigned: '#00A6A0',
    in_progress: '#F97316',
    completed: '#A8E6CF',
    cancelled: '#94A3B8',
    no_show: '#FF6F61',
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="shift-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">
              Shift Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Create, assign, and track shifts with location mapping
            </p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5 mr-2">
                <Button
                  size="sm"
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  className={`text-xs h-8 px-3 gap-1.5 ${viewMode === 'calendar' ? 'bg-[#00A6A0] text-white' : 'text-muted-foreground'}`}
                  onClick={() => setViewMode('calendar')}
                  data-testid="toggle-calendar-view"
                >
                  <LayoutGrid className="w-3.5 h-3.5" /> Calendar
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  className={`text-xs h-8 px-3 gap-1.5 ${viewMode === 'list' ? 'bg-[#00A6A0] text-white' : 'text-muted-foreground'}`}
                  onClick={() => setViewMode('list')}
                  data-testid="toggle-list-view"
                >
                  <List className="w-3.5 h-3.5" /> List
                </Button>
              </div>
              <Button className="btn-primary" onClick={() => setShowCreateDialog(true)} data-testid="create-shift-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Shift
              </Button>
            </div>
          )}
        </div>

        {/* Filters - List view only */}
        {viewMode === 'list' && (
        <div className="flex flex-wrap gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-border bg-card text-foreground px-3 py-1.5 text-sm"
            data-testid="status-filter"
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="assigned">Assigned</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="no_show">No Show</option>
          </select>
          <Input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-auto bg-card border-border"
            data-testid="date-filter"
          />
          {(statusFilter || dateFilter) && (
            <Button variant="ghost" size="sm" onClick={() => { setStatusFilter(''); setDateFilter(''); }} className="text-[#00A6A0]">
              <X className="w-4 h-4 mr-1" />Clear
            </Button>
          )}
        </div>
        )}

        {/* Calendar View */}
        {viewMode === 'calendar' && isAdmin && (
          <ShiftCalendar
            shifts={calendarShifts}
            onReschedule={handleReschedule}
            onShiftClick={handleCalendarShiftClick}
          />
        )}

        {/* No-Show Alerts Banner */}
        {isAdmin && noShowAlerts.filter(a => a.status === 'unresolved').length > 0 && (
          <Card className="border-red-500/30 bg-red-500/5" data-testid="no-show-alerts-banner">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{noShowAlerts.filter(a => a.status === 'unresolved').length} No-Show Alert(s)</p>
                    <p className="text-xs text-muted-foreground">Caregivers who didn't check in for their shifts</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="border-red-500/30 text-red-500" onClick={() => setShowNoShows(!showNoShows)} data-testid="toggle-no-show-alerts">
                  {showNoShows ? 'Hide' : 'View'}
                </Button>
              </div>
              {showNoShows && (
                <div className="mt-4 space-y-2">
                  {noShowAlerts.filter(a => a.status === 'unresolved').map((alert, i) => (
                    <div key={alert.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border" data-testid={`no-show-alert-${i}`}>
                      <div>
                        <p className="text-sm font-medium text-foreground">{alert.caregiver_name || 'Unknown'}</p>
                        <p className="text-xs text-muted-foreground">{alert.date} at {alert.scheduled_start} — Client: {alert.client_name || 'N/A'}</p>
                      </div>
                      <Button size="sm" variant="outline" className="border-border text-foreground" onClick={() => handleResolveNoShow(alert.id, 'Acknowledged by admin')} data-testid={`resolve-no-show-${i}`}>
                        Resolve
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Shifts List - List view only */}
        {viewMode === 'list' && (isLoading ? (
          <div className="text-center py-12"><div className="spinner mx-auto" /></div>
        ) : shifts.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="p-12 text-center">
              <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-heading font-semibold text-foreground mb-2">No Shifts Found</h3>
              <p className="text-muted-foreground">
                {statusFilter || dateFilter ? 'Try changing your filters' : 'Create your first shift to get started'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {shifts.map(shift => {
              const isExpanded = expandedShift === shift.id;
              const color = statusColors[shift.status] || '#94A3B8';
              return (
                <Card key={shift.id} className="glass-card" data-testid={`shift-card-${shift.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${color}15` }}>
                          <Calendar className="w-5 h-5" style={{ color }} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading font-semibold text-foreground">
                              {shift.date}
                            </h3>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                              style={{ backgroundColor: `${color}15`, color }}>
                              {shift.status?.replace('_', ' ')}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 mt-1.5 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {shift.start_time} — {shift.end_time}
                            </span>
                            {shift.client_name && (
                              <span className="flex items-center gap-1">
                                <User className="w-3.5 h-3.5" />
                                Client: {shift.client_name}
                              </span>
                            )}
                            {shift.caregiver_name && (
                              <span className="flex items-center gap-1 text-[#00A6A0]">
                                <User className="w-3.5 h-3.5" />
                                {shift.caregiver_name}
                              </span>
                            )}
                          </div>
                          {shift.address && (
                            <p className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                              <MapPin className="w-3.5 h-3.5 text-[#FF6F61] flex-shrink-0" />
                              {shift.address}
                            </p>
                          )}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setExpandedShift(isExpanded ? null : shift.id)}>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t border-border space-y-4 animate-in slide-in-from-top-1">
                        {/* Map */}
                        {shift.latitude && shift.longitude && (
                          <div data-testid={`shift-map-${shift.id}`}>
                            <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-[#FF6F61]" />Location
                            </h4>
                            <ShiftMap
                              latitude={shift.latitude}
                              longitude={shift.longitude}
                              address={shift.address}
                              height="250px"
                            />
                          </div>
                        )}

                        {shift.notes && (
                          <div>
                            <h4 className="text-sm font-semibold text-foreground mb-1">Notes</h4>
                            <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">{shift.notes}</p>
                          </div>
                        )}

                        {shift.rate && (
                          <p className="text-sm text-muted-foreground">
                            <span className="font-medium text-foreground">Rate:</span> £{shift.rate}/hr
                          </p>
                        )}

                        {/* Assign caregiver (admin only, open shifts) */}
                        {isAdmin && shift.status === 'open' && caregivers.length > 0 && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-sm font-semibold text-foreground">Assign Caregiver</h4>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fetchSuggestions(shift.id)}
                                className="text-xs gap-1.5 border-[#14B8A6]/30 text-[#14B8A6] hover:bg-[#14B8A6]/10"
                                data-testid={`smart-suggest-btn-${shift.id}`}
                              >
                                <Sparkles className="w-3.5 h-3.5" /> Smart Suggest
                              </Button>
                            </div>
                            <div className="flex gap-2">
                              <select
                                className="flex-1 rounded-md border border-border bg-card text-foreground px-3 py-1.5 text-sm"
                                onChange={(e) => { if (e.target.value) handleAssignCaregiver(shift.id, e.target.value); }}
                                defaultValue=""
                                data-testid={`assign-caregiver-${shift.id}`}
                              >
                                <option value="" disabled>Select caregiver...</option>
                                {caregivers.map(cg => (
                                  <option key={cg.id} value={cg.id}>{cg.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}

                        {/* Broadcast & Bids (admin only, open shifts) */}
                        {isAdmin && shift.status === 'open' && (
                          <div className="flex flex-wrap items-center gap-2 pt-1">
                            {shift.broadcast_status !== 'broadcast' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setBroadcastingShift(shift.id)}
                                className="text-xs gap-1.5 border-[#FF6F61]/30 text-[#FF6F61] hover:bg-[#FF6F61]/10"
                                data-testid={`broadcast-btn-${shift.id}`}
                              >
                                <Radio className="w-3.5 h-3.5" /> Broadcast
                              </Button>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-[#FF6F61] flex items-center gap-1">
                                  <Radio className="w-3 h-3" /> Broadcast
                                  {shift.broadcast_urgency === 'urgent' && (
                                    <span className="bg-red-500/20 text-red-400 text-[10px] px-1.5 py-0.5 rounded-full">URGENT</span>
                                  )}
                                </span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewBids(shift.id)}
                                  className="text-xs gap-1.5 border-[#4A90E2]/30 text-[#4A90E2] hover:bg-[#4A90E2]/10"
                                  data-testid={`view-bids-btn-${shift.id}`}
                                >
                                  <Users className="w-3.5 h-3.5" /> View Bids
                                </Button>
                              </div>
                            )}
                          </div>
                        )}

                        {shift.check_in && (
                          <p className="text-sm text-[#00A6A0] flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Checked in at {new Date(shift.check_in).toLocaleTimeString()}
                          </p>
                        )}
                        {shift.check_out && (
                          <p className="text-sm text-[#00A6A0] flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Checked out at {new Date(shift.check_out).toLocaleTimeString()}
                            {shift.hours_worked && ` (${shift.hours_worked.toFixed(2)}h)`}
                          </p>
                        )}

                        {/* Caregiver Clock In/Out Buttons */}
                        {!isAdmin && user?.role === 'caregiver' && shift.status === 'assigned' && !shift.check_in && (
                          <Button
                            className="bg-[#00A6A0] hover:bg-[#00A6A0]/90 text-white"
                            onClick={() => handleClockIn(shift.id)}
                            disabled={clockingIn === shift.id}
                            data-testid={`clock-in-btn-${shift.id}`}
                          >
                            {clockingIn === shift.id ? (
                              <><div className="spinner w-4 h-4 mr-2" />Getting GPS...</>
                            ) : (
                              <><Navigation className="w-4 h-4 mr-2" />Clock In (GPS)</>
                            )}
                          </Button>
                        )}
                        {!isAdmin && user?.role === 'caregiver' && shift.status === 'in_progress' && shift.check_in && !shift.check_out && (
                          <div className="flex items-center gap-2">
                            <Button
                              className="bg-[#FF6F61] hover:bg-[#FF6F61]/90 text-white"
                              onClick={() => handleClockOut(shift.id)}
                              disabled={clockingOut === shift.id}
                              data-testid={`clock-out-btn-${shift.id}`}
                            >
                              {clockingOut === shift.id ? (
                                <><div className="spinner w-4 h-4 mr-2" />Getting GPS...</>
                              ) : (
                                <><LogOut className="w-4 h-4 mr-2" />Clock Out (GPS)</>
                              )}
                            </Button>
                            <span className="text-xs text-muted-foreground italic">Care report must be submitted first</span>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ))}
      </div>

      {/* Create Shift Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-foreground">Create New Shift</DialogTitle>
          </DialogHeader>

          {/* Template Picker */}
          {templates.length > 0 && (
            <div className="space-y-2" data-testid="template-picker">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Quick Templates</p>
              <div className="flex flex-wrap gap-2">
                {templates.map(t => (
                  <div key={t.id} className="group relative" data-testid={`template-${t.id}`}>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-[#00A6A0]/30 text-foreground hover:bg-[#00A6A0]/10 hover:border-[#00A6A0] text-xs h-8 gap-1.5 pr-7"
                      onClick={() => applyTemplate(t)}
                      data-testid={`apply-template-${t.id}`}
                    >
                      <Bookmark className="w-3 h-3 text-[#00A6A0]" />
                      {t.name}
                      <span className="text-muted-foreground ml-1">{t.start_time}-{t.end_time}</span>
                    </Button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id, t.name); }}
                      className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-template-${t.id}`}
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleCreateShift} className="space-y-4 py-2">
            <div>
              <Label className="text-foreground">Client *</Label>
              <select
                value={shiftForm.client_id}
                onChange={(e) => setShiftForm(p => ({ ...p, client_id: e.target.value }))}
                className="w-full mt-1 rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm"
                required
                data-testid="shift-client-select"
              >
                <option value="">Select client...</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-foreground">Date *</Label>
                <Input type="date" value={shiftForm.date}
                  onChange={(e) => setShiftForm(p => ({ ...p, date: e.target.value }))}
                  className="bg-background border-border mt-1" required data-testid="shift-date-input" />
              </div>
              <div>
                <Label className="text-foreground">Start *</Label>
                <Input type="time" value={shiftForm.start_time}
                  onChange={(e) => setShiftForm(p => ({ ...p, start_time: e.target.value }))}
                  className="bg-background border-border mt-1" required data-testid="shift-start-input" />
              </div>
              <div>
                <Label className="text-foreground">End *</Label>
                <Input type="time" value={shiftForm.end_time}
                  onChange={(e) => setShiftForm(p => ({ ...p, end_time: e.target.value }))}
                  className="bg-background border-border mt-1" required data-testid="shift-end-input" />
              </div>
            </div>

            <div>
              <Label className="text-foreground">Hourly Rate (£)</Label>
              <Input type="number" step="0.01" value={shiftForm.rate}
                onChange={(e) => setShiftForm(p => ({ ...p, rate: e.target.value }))}
                placeholder="15.00"
                className="bg-background border-border mt-1" data-testid="shift-rate-input" />
            </div>

            <div>
              <Label className="text-foreground">Job Visibility</Label>
              <select
                value={shiftForm.visibility}
                onChange={(e) => setShiftForm(p => ({ ...p, visibility: e.target.value }))}
                className="w-full mt-1 rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm"
                data-testid="shift-visibility-select"
              >
                <option value="network">My Network Only — visible to caregivers in your agency</option>
                <option value="global">All FendanaCura Users — visible to all caregivers on the platform</option>
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">
                {shiftForm.visibility === 'network' ? 'Only caregivers registered with your agency will see this shift.' : 'All caregivers on FendanaCura can see and bid on this shift.'}
              </p>
            </div>

            <div>
              <Label className="text-foreground">Shift Address</Label>
              <div className="flex gap-2 mt-1">
                <Input value={shiftForm.address}
                  onChange={(e) => setShiftForm(p => ({ ...p, address: e.target.value, latitude: null, longitude: null }))}
                  placeholder="123 Care Street, London, UK"
                  className="bg-background border-border flex-1"
                  data-testid="shift-address-input" />
                <Button type="button" variant="outline" onClick={handleGeocodeAddress}
                  disabled={geocoding || !shiftForm.address}
                  className="btn-secondary whitespace-nowrap"
                  data-testid="geocode-address-btn">
                  {geocoding ? (
                    <div className="spinner w-4 h-4" />
                  ) : (
                    <><MapPin className="w-4 h-4 mr-1" />Locate</>
                  )}
                </Button>
              </div>
              {shiftForm.latitude && shiftForm.longitude && (
                <div className="mt-3">
                  <p className="text-xs text-[#00A6A0] mb-2 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Location found ({shiftForm.latitude.toFixed(4)}, {shiftForm.longitude.toFixed(4)})
                  </p>
                  <ShiftMap latitude={shiftForm.latitude} longitude={shiftForm.longitude} address={shiftForm.address} height="180px" />
                </div>
              )}
            </div>

            <div>
              <Label className="text-foreground">Notes</Label>
              <textarea value={shiftForm.notes}
                onChange={(e) => setShiftForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Special instructions, client preferences..."
                rows={3}
                className="w-full mt-1 rounded-md border border-border bg-background text-foreground px-3 py-2 text-sm resize-none"
                data-testid="shift-notes-input" />
            </div>

            {/* Conflict Warning */}
            {conflicts.length > 0 && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="conflict-warning">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  <p className="text-sm font-medium text-amber-500">{conflicts.length} Scheduling Conflict{conflicts.length > 1 ? 's' : ''} Detected</p>
                </div>
                <div className="space-y-1.5 pl-6">
                  {conflicts.map((c, i) => (
                    <div key={i} className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid={`conflict-item-${i}`}>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${c.type === 'caregiver' ? 'bg-[#FF6F61]' : 'bg-[#00A6A0]'}`} />
                      <span>{c.message}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground pl-6">You can still create this shift — conflicts are warnings only.</p>
              </div>
            )}

            <Button type="submit" className="w-full btn-primary py-5" data-testid="save-shift-btn">
              Create Shift
            </Button>

            {/* Save as Template */}
            {shiftForm.start_time && shiftForm.end_time && (
              <div data-testid="save-template-section">
                {!showSaveTemplate ? (
                  <Button type="button" variant="ghost" size="sm" className="w-full text-xs text-muted-foreground hover:text-foreground gap-1.5" onClick={() => setShowSaveTemplate(true)} data-testid="show-save-template-btn">
                    <Save className="w-3 h-3" /> Save these times as a template
                  </Button>
                ) : (
                  <div className="flex gap-2 items-center">
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="Template name (e.g. Morning Care)"
                      className="bg-background border-border text-sm h-8 flex-1"
                      data-testid="template-name-input"
                    />
                    <Button type="button" size="sm" className="btn-primary h-8 text-xs gap-1" onClick={saveAsTemplate} data-testid="confirm-save-template-btn">
                      <Save className="w-3 h-3" /> Save
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }} data-testid="cancel-save-template-btn">
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Smart Suggest Dialog */}
      <Dialog open={showSuggestions} onOpenChange={(open) => { setShowSuggestions(open); if (!open) setSuggestions(null); }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="smart-suggest-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading text-foreground flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#14B8A6]" /> Smart-Suggest Caregiver
            </DialogTitle>
          </DialogHeader>
          {suggestLoading ? (
            <div className="flex flex-col items-center gap-3 py-10">
              <div className="spinner w-8 h-8" />
              <p className="text-sm text-muted-foreground">Analyzing availability, proximity & compliance...</p>
            </div>
          ) : suggestions ? (
            <div className="space-y-4 py-2">
              {/* Shift Info */}
              <div className="p-3 rounded-lg bg-muted border border-border">
                <p className="text-sm text-foreground font-medium">{suggestions.client_name}</p>
                <p className="text-xs text-muted-foreground">{suggestions.date} | {suggestions.time}</p>
              </div>

              {/* AI Recommendation */}
              {suggestions.ai_recommendation && (
                <div className="p-3 rounded-lg bg-[#14B8A6]/5 border border-[#14B8A6]/20" data-testid="ai-recommendation">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-[#14B8A6] mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-foreground">{suggestions.ai_recommendation}</p>
                  </div>
                </div>
              )}

              {/* Ranked Caregivers */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
                  Ranked Caregivers ({suggestions.suggestions?.length || 0} of {suggestions.total_caregivers})
                </p>
                {suggestions.suggestions?.map((s, i) => (
                  <div
                    key={s.caregiver_id}
                    className={`p-3 rounded-lg border transition-all ${
                      !s.available ? 'border-red-500/20 bg-red-500/5 opacity-60' :
                      i === 0 ? 'border-[#14B8A6]/40 bg-[#14B8A6]/5' : 'border-border hover:border-[#384752]'
                    }`}
                    data-testid={`suggestion-${s.caregiver_id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {i === 0 && s.available && <Star className="w-4 h-4 text-[#14B8A6] fill-[#14B8A6]" />}
                        <span className="text-foreground font-medium text-sm">{s.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          s.score >= 70 ? 'bg-[#A8E6CF]/20 text-[#A8E6CF]' :
                          s.score >= 40 ? 'bg-amber-400/20 text-amber-400' :
                          'bg-red-400/20 text-red-400'
                        }`}>{s.score}%</span>
                      </div>
                      {s.available && (
                        <Button
                          size="sm"
                          className="btn-primary text-xs h-7"
                          onClick={() => handleAssignFromSuggestion(suggestions.shift_id, s.caregiver_id)}
                          data-testid={`assign-suggested-${s.caregiver_id}`}
                        >
                          Assign
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {s.reasons?.map((r, j) => (
                        <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{r}</span>
                      ))}
                    </div>
                    {!s.compliant && s.missing_docs?.length > 0 && (
                      <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Missing: {s.missing_docs.slice(0, 2).join(', ')}{s.missing_docs.length > 2 ? ` +${s.missing_docs.length - 2}` : ''}
                      </p>
                    )}
                  </div>
                ))}
                {(!suggestions.suggestions || suggestions.suggestions.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">No caregivers found in this organization.</p>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Broadcast Shift Dialog */}
      <Dialog open={!!broadcastingShift} onOpenChange={(open) => { if (!open) { setBroadcastingShift(null); setBroadcastMsg(''); setBroadcastUrgency('normal'); } }}>
        <DialogContent className="glass-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Radio className="w-5 h-5 text-[#FF6F61]" /> Broadcast Shift
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-muted-foreground">
              This will notify all caregivers in your network about this available shift. They can then bid to express their interest.
            </p>
            <div>
              <Label className="text-xs">Urgency</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  variant={broadcastUrgency === 'normal' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setBroadcastUrgency('normal')}
                  className={broadcastUrgency === 'normal' ? 'bg-[#00A6A0] text-white' : ''}
                  data-testid="urgency-normal-btn"
                >
                  Normal
                </Button>
                <Button
                  variant={broadcastUrgency === 'urgent' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setBroadcastUrgency('urgent')}
                  className={broadcastUrgency === 'urgent' ? 'bg-[#FF6F61] text-white' : ''}
                  data-testid="urgency-urgent-btn"
                >
                  Urgent
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Message (optional)</Label>
              <textarea
                value={broadcastMsg}
                onChange={e => setBroadcastMsg(e.target.value)}
                placeholder="Add context for caregivers e.g. 'Urgent cover needed, regular caregiver is unwell'"
                className="w-full mt-1 rounded-lg bg-black/20 border border-border/50 text-foreground p-3 text-sm min-h-[80px] resize-y focus:outline-none"
                data-testid="broadcast-message-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBroadcastingShift(null)} data-testid="broadcast-cancel-btn">Cancel</Button>
              <Button className="bg-[#FF6F61] hover:bg-[#e55a4d] text-white" onClick={() => handleBroadcastShift(broadcastingShift)} data-testid="broadcast-confirm-btn">
                <Radio className="w-4 h-4 mr-2" /> Broadcast Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Bids Dialog */}
      <Dialog open={!!viewBidsShift} onOpenChange={(open) => { if (!open) { setViewBidsShift(null); setBids([]); setAiRecommendation(null); } }}>
        <DialogContent className="glass-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Users className="w-5 h-5 text-[#4A90E2]" /> Smart Match — Bids
            </DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            {bidsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="spinner w-6 h-6" />
              </div>
            ) : bids.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No bids received yet</p>
                <p className="text-xs text-muted-foreground mt-1">Caregivers will appear here once they express interest</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* AI Recommendation */}
                {aiRecommendation && (
                  <div className="p-3 rounded-lg bg-[#4A90E2]/10 border border-[#4A90E2]/20" data-testid="ai-recommendation">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-3.5 h-3.5 text-[#4A90E2]" />
                      <span className="text-xs font-semibold text-[#4A90E2]">AI Recommendation</span>
                    </div>
                    <p className="text-xs text-foreground/80">{aiRecommendation}</p>
                  </div>
                )}

                <div className="space-y-2 max-h-[380px] overflow-y-auto" data-testid="bids-list">
                  {bids.map(bid => (
                    <Card key={bid.id} className={`glass-card overflow-hidden ${bid.recommended ? 'border-[#00A6A0]/50 ring-1 ring-[#00A6A0]/20' : bid.status === 'accepted' ? 'border-[#00A6A0]/40' : bid.status === 'rejected' ? 'opacity-50' : 'border-border'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-foreground">{bid.caregiver_name}</span>
                              {bid.recommended && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#00A6A0]/15 text-[#00A6A0] font-semibold" data-testid={`recommended-badge-${bid.id}`}>
                                  TOP MATCH
                                </span>
                              )}
                              {!bid.available && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400">BUSY</span>
                              )}
                              {!bid.compliant && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400">DOCS PENDING</span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1.5">
                              <div className="flex items-center gap-1">
                                <div className="w-8 h-1.5 rounded-full bg-muted overflow-hidden">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${bid.match_score}%`, backgroundColor: bid.match_score >= 70 ? '#00A6A0' : bid.match_score >= 40 ? '#F59E0B' : '#FF6F61' }} />
                                </div>
                                <span className="text-[10px] font-mono text-muted-foreground" data-testid={`score-${bid.id}`}>{bid.match_score}</span>
                              </div>
                              {bid.distance_km != null && (
                                <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                                  <MapPin className="w-2.5 h-2.5" />{bid.distance_km}km
                                </span>
                              )}
                              {bid.shifts_with_client > 0 && (
                                <span className="text-[10px] text-muted-foreground">{bid.shifts_with_client} past shifts</span>
                              )}
                            </div>
                            {bid.message && (
                              <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1">
                                <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" /> {bid.message}
                              </p>
                            )}
                            {bid.reasons && bid.reasons.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {bid.reasons.slice(0, 3).map((r, i) => (
                                  <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{r}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="shrink-0">
                            {bid.status === 'pending' && (
                              <Button
                                size="sm"
                                className={bid.recommended ? 'bg-[#00A6A0] hover:bg-[#008A85] text-white text-xs h-7' : 'btn-primary text-xs h-7'}
                                onClick={() => handleAcceptBid(viewBidsShift, bid.id, bid.caregiver_name)}
                                data-testid={`accept-bid-${bid.id}`}
                              >
                                {bid.recommended ? 'Accept Match' : 'Accept'}
                              </Button>
                            )}
                            {bid.status === 'accepted' && (
                              <span className="text-xs text-[#00A6A0] flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Accepted</span>
                            )}
                            {bid.status === 'rejected' && (
                              <span className="text-xs text-muted-foreground">Not selected</span>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default ShiftManagement;
